import React, { useEffect, useMemo, useState } from 'react';

export interface TourStep {
  selector: string;
  title: string;
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

interface TourOverlayProps {
  open: boolean;
  steps: TourStep[];
  stepIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT = 180;
const VIEWPORT_PADDING = 16;
const HIGHLIGHT_PADDING = 10;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export default function TourOverlay({
  open,
  steps,
  stepIndex,
  onClose,
  onNext,
  onPrev,
}: TourOverlayProps) {
  const [targetBox, setTargetBox] = useState<Box | null>(null);
  const step = steps[stepIndex] || null;

  useEffect(() => {
    if (!open || !step) {
      setTargetBox(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (!el) {
        setTargetBox(null);
        return;
      }

      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      const rect = el.getBoundingClientRect();
      setTargetBox({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    update();
    const timer = window.setTimeout(update, 180);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, step]);

  const highlightStyle = useMemo(() => {
    if (!targetBox) return null;
    return {
      top: `${targetBox.top - HIGHLIGHT_PADDING}px`,
      left: `${targetBox.left - HIGHLIGHT_PADDING}px`,
      width: `${targetBox.width + HIGHLIGHT_PADDING * 2}px`,
      height: `${targetBox.height + HIGHLIGHT_PADDING * 2}px`,
    };
  }, [targetBox]);

  const tooltipStyle = useMemo(() => {
    const viewportWidth = window.innerWidth || 1280;
    const viewportHeight = window.innerHeight || 720;
    if (!targetBox || !step) {
      return {
        top: `${(viewportHeight - TOOLTIP_HEIGHT) / 2}px`,
        left: `${(viewportWidth - TOOLTIP_WIDTH) / 2}px`,
      };
    }

    const placement = step.placement || 'bottom';
    const targetCenterX = targetBox.left + targetBox.width / 2;
    const targetCenterY = targetBox.top + targetBox.height / 2;

    let top = targetBox.top + targetBox.height + 18;
    let left = targetCenterX - TOOLTIP_WIDTH / 2;

    if (placement === 'top') {
      top = targetBox.top - TOOLTIP_HEIGHT - 18;
      left = targetCenterX - TOOLTIP_WIDTH / 2;
    } else if (placement === 'left') {
      top = targetCenterY - TOOLTIP_HEIGHT / 2;
      left = targetBox.left - TOOLTIP_WIDTH - 18;
    } else if (placement === 'right') {
      top = targetCenterY - TOOLTIP_HEIGHT / 2;
      left = targetBox.left + targetBox.width + 18;
    }

    const clampedTop = clamp(
      top,
      VIEWPORT_PADDING,
      viewportHeight - TOOLTIP_HEIGHT - VIEWPORT_PADDING,
    );
    const clampedLeft = clamp(
      left,
      VIEWPORT_PADDING,
      viewportWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING,
    );

    return {
      top: `${clampedTop}px`,
      left: `${clampedLeft}px`,
    };
  }, [targetBox, step]);

  if (!open || !step) return null;

  return (
    <div className="tour-overlay">
      {highlightStyle && <div className="tour-highlight" style={highlightStyle} />}

      <div className="tour-tooltip" style={tooltipStyle} key={stepIndex}>
        <div className="tour-step-counter">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <h4>{step.title}</h4>
        <p>{step.description}</p>

        <div className="tour-actions">
          <button className="btn-secondary" onClick={onClose}>
            Skip
          </button>
          <button className="btn-secondary" onClick={onPrev} disabled={stepIndex === 0}>
            Back
          </button>
          <button className="btn-primary" onClick={onNext}>
            {stepIndex === steps.length - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
